import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchasingAPI } from '../../../lib/api';
import type { PurchaseOrderLine } from '../../../lib/api/types/po-types';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { usePortalMode } from '../../../hooks/usePortalMode';
import { getCounterpartyLabel } from '../../../lib/portalLabels';
import { Button } from '../../ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { Loader2, AlertCircle, X, ExternalLink, Download, ShieldAlert } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PROCESSING_STAGE_CONFIG } from '../../purchasing/tabs/po-lines/types';
import { useAuth } from '../../../contexts/AuthContext';
import OTDOverridePanel from '../../purchasing/OTDOverridePanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

/**
 * Processing stages that must NEVER participate in OTD classification — the
 * frontend mirror of the backend single source of truth (OTD_EXCLUDED_STAGES
 * in analytics.sales_orders_dashboard). A line in one of these stages is never
 * Late/Early/On-Time:
 *   - 'docked'    = work not started; the customer's parts may not even have
 *                   arrived — not a delivery failure.
 *   - 'cancelled' = the line will never be delivered — not a delivery outcome.
 * Such lines still appear under the "All" tab with their status badge.
 */
const OTD_EXCLUDED_STAGES = new Set(['docked', 'cancelled']);

/**
 * Supplier Promise Date — the sole due date for OTD / delivery scoring.
 * Lines with NULL supplier_promise_date are excluded from on-time buckets
 * and must not fall back to delivery_date (Agreed Date / ODD).
 */
function dueDateOf(ln: PurchaseOrderLine): string | null | undefined {
  return ln.supplier_promise_date ?? null;
}

/**
 * Effective DELIVERY (dispatch) date for on-time classification — the date
 * goods were dispatched (delivery_lines), which is what OTD measures against.
 * NOT last_received_date ("Order was received" = order intake). Returns null
 * when nothing was dispatched so the line falls outside early/on_time/late.
 */
function receiptDateOf(ln: PurchaseOrderLine): string | null | undefined {
  return Number(ln.dispatched_qty) > 0 ? ln.dispatched_date : null;
}

/** Resolve a line's Status badge label + classes (mirrors the SO Lines grid). */
function stageLabelOf(ln: PurchaseOrderLine): { label: string; className: string } {
  const stage = ln.on_hold ? 'on_hold' : (ln.processing_stage || '');
  const cfg = PROCESSING_STAGE_CONFIG[stage];
  return {
    label: cfg?.label || ln.processing_stage || '—',
    className: cfg?.className || 'bg-slate-100 text-slate-700',
  };
}

/** On-time sub-tabs shown inside every delivery drill-down. */
const TIMING_TABS: { key: 'all' | 'on_time' | 'early' | 'late'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'on_time', label: 'On Time' },
  { key: 'early', label: 'Early' },
  { key: 'late', label: 'Late' },
];
type TimingTabKey = (typeof TIMING_TABS)[number]['key'];

/**
 * Classify a line into an on-time bucket by comparing receipt vs due date
 * (ISO 'YYYY-MM-DD' strings compare correctly). Lines not yet received — or
 * with no due date — fall outside on_time/early/late and only appear under All.
 *
 * Docked AND Cancelled lines are NEVER classified Late/Early/On-Time: a docked
 * line is pre-processing (work not started — the customer's parts may not even
 * have arrived) and a cancelled line will never be delivered, so neither is a
 * delivery outcome and must not drag any OTD bucket. This mirrors the backend
 * OTD donut / is_late filter (both exclude OTD_EXCLUDED_STAGES). Such a past-due
 * line still shows under "All" with its status badge.
 */
/** Add `days` to an ISO 'YYYY-MM-DD' date string, returning ISO. */
function addIsoDays(iso: string, days: number): string {
  if (!days) return iso;
  const dt = new Date(`${iso}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function timingBucketOf(ln: PurchaseOrderLine, graceDays = 1): TimingTabKey | 'other' {
  const due = dueDateOf(ln);
  const received = receiptDateOf(ln);
  if (!due) return 'other';
  // Lines in the OTD exclusion list never enter early/on_time/late, regardless
  // of whether they were dispatched — they only appear under "All".
  if (ln.processing_stage && OTD_EXCLUDED_STAGES.has(ln.processing_stage)) return 'other';
  // Held lines are excluded from OTD (mirrors the donut / on_time_status filter).
  if (ln.on_hold) return 'other';
  const d = due.slice(0, 10);
  const todayIso = new Date().toISOString().slice(0, 10);
  // Measurable = past-due OR actually delivered; a not-yet-due, not-yet-
  // delivered line is pending and shown only under "All".
  const measurable = d < todayIso || !!received;
  if (!measurable) return 'other';
  // within_firm — mirrors _bucketed.py exactly: a manual OTD override wins
  // (verbatim verdict); otherwise an on-time dispatch (received ≤ due) OR a
  // shipped line with no dispatch date (shipped-as-on-time exemption).
  let withinFirm: boolean;
  if (ln.otd_overridden) {
    withinFirm = !!ln.otd_override_value;
  } else if (received) {
    // Grace buffer: dispatched up to `graceDays` after the promise = on-time.
    withinFirm = received.slice(0, 10) <= addIsoDays(d, graceDays);
  } else {
    withinFirm = ln.processing_stage === 'shipped';
  }
  if (!withinFirm) return 'late';
  // within_firm split: early when the dispatch was before the promise date.
  if (received && received.slice(0, 10) < d) return 'early';
  return 'on_time';
}

type SortKey =
  | 'po' | 'line' | 'site' | 'counterparty' | 'part' | 'due'
  | 'received' | 'ontime' | 'qty' | 'outstanding' | 'value' | 'status';

/** Comparable value for a line under a given sort column. */
function sortValueOf(ln: PurchaseOrderLine, key: SortKey): string | number {
  switch (key) {
    case 'po': return (ln.po_number || '').toLowerCase();
    case 'line': return Number(ln.line_number) || 0;
    case 'site': return (ln.supplier_name || '').toLowerCase();
    case 'counterparty': return (ln.customer_name || '').toLowerCase();
    case 'part': return (ln.part_number || '').toLowerCase();
    case 'due': return (dueDateOf(ln) || '').slice(0, 10);
    case 'received': return (ln.dispatched_date || '').slice(0, 10);
    case 'ontime': return deriveTiming(receiptDateOf(ln), dueDateOf(ln)).label;
    case 'qty': return Number(ln.quantity) || 0;
    case 'outstanding': return Number(ln.outstanding_qty) || 0;
    case 'value': return parseFloat(ln.total_price || '0') || 0;
    case 'status': return stageLabelOf(ln).label;
  }
}

/**
 * Drill-down scope keys. Each maps to a canonical filter set on the
 * `/purchasing/lines/` endpoint (see SCOPE_PARAMS below). Stream 1 owns
 * adding the `is_arrears`, `is_late`, and `last_received_date_from/to`
 * filter params on the backend — until those land we send the
 * request anyway and rely on the empty-state to render gracefully.
 */
export type DrillDownScope =
  | 'open'
  | 'open_all'
  | 'outstanding'
  | 'on_hold'
  | 'wip'
  | 'docked'
  | 'completed'
  | 'arrears'
  | 'late'
  | 'otif_miss'
  | 'received'
  | 'delivered'
  | 'otd';

export interface DrillDownPeriod {
  /** ISO 'YYYY-MM-DD' inclusive lower bound, or null for no lower bound. */
  start: string | null;
  /** ISO 'YYYY-MM-DD' inclusive upper bound, or null for no upper bound. */
  end: string | null;
  /** Human label shown in the panel header (e.g. 'this week', 'YTD'). */
  label: string;
}

export interface DrillDownExtraFilters {
  /** Subset of GRN on-time-status: 'on_time' | 'early' | 'late'. */
  on_time_status?: 'on_time' | 'early' | 'late';
  /** Day-of-week, used for the deliveries-by-day chart drill-in. */
  weekday?: string;
  /** Pass-through extras (any other filter param the backend accepts). */
  [key: string]: string | number | boolean | undefined;
}

export interface SalesOrdersDrillDownProps {
  isOpen: boolean;
  onClose: () => void;
  scope: DrillDownScope | null;
  period: DrillDownPeriod | null;
  extraFilters?: DrillDownExtraFilters;
  /** Header title (e.g. 'Open Order Lines'). */
  title: string;
  /** Optional subtitle shown beneath the title. */
  subtitle?: string;
  /** Optional override for the customer filter (for context-scoping). */
  customerId?: string;
  /** Optional override for the supplier filter. */
  supplierId?: number | string;
  /** Optional part-number passthrough. */
  partNumber?: string;
  /**
   * OTD grace buffer in days (from the dashboard payload). A dispatch up to this
   * many days after the promise still classifies on-time — must match the
   * backend so the timing sub-tabs reconcile with the donut/by-day bars.
   */
  graceDays?: number;
}

/** Map a scope to the param payload required by `/purchasing/lines/`. */
function buildScopeParams(
  scope: DrillDownScope,
  period: DrillDownPeriod | null,
): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {};
  switch (scope) {
    case 'open':
      // Open Order Lines = LIVE, not-yet-late: visibly open (On Hold / Docked /
      // WIP via open_view), outstanding>0, due today or later. status__in stays
      // as the stale-outstanding defense. Matches portfolio_open_value exactly
      // and is disjoint from Arrears (visibly-open & overdue). No date window.
      params['open_view'] = true;
      params['status__in'] = 'pending,partially_received';
      params['is_open'] = true;
      params['not_overdue'] = true;
      break;
    case 'open_all':
      // ORDER STATUS "Open Order Lines" = the whole open book, defined EXACTLY
      // like order_status.open_count and the PO Lines grid Open tab: the
      // VISIBLE Status column reads On Hold / Docked / WIP (on_hold flag OR
      // processing_stage in docked/wip). open_view applies that OR server-side
      // so the count and drill-down can't diverge. (The Cost 'open' scope above
      // is the not-yet-late, outstanding>0 value subset.)
      params['open_view'] = true;
      break;
    case 'on_hold':
      // On Hold — the on_hold flag (Status column overlay). Scoped to due window.
      params['on_hold'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'wip':
      // WIP — Status column = processing_stage 'wip', not held.
      params['processing_stage'] = 'wip';
      params['not_on_hold'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'docked':
      // Status-split donut 'Docked' slice — processing_stage 'docked', not held.
      params['processing_stage'] = 'docked';
      params['not_on_hold'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'completed':
      // Status-split donut 'Completed' slice — processing_stage 'completed', not held.
      params['processing_stage'] = 'completed';
      params['not_on_hold'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'arrears':
      // Arrears = ALL overdue live lines (portfolio), matching the headline
      // Arrears tile (portfolio_arrears_value). A line is in arrears whenever
      // it's overdue, regardless of which week it was due — so no date window.
      // The backend is_arrears filter applies the live + overdue-by-promise rule.
      params['is_arrears'] = true;
      break;
    case 'late':
      // OTD "misses" — past-due lines not received on time. Window on Supplier
      // Promise Date via due_from/due_to (null promise dates excluded).
      params['is_late'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'otif_miss':
      // OTIF "misses" — past-due lines not delivered on-time-AND-in-full
      // (includes on-time-but-short deliveries, unlike the OTD 'late' scope).
      params['is_otif_miss'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'received':
      // Dispatch-windowed: lines actually DISPATCHED in the period (by-day
      // chart keys on the delivery date, not the order-intake date).
      params['received'] = true;
      if (period?.start) params['dispatched_date_from'] = period.start;
      if (period?.end) params['dispatched_date_to'] = period.end;
      break;
    case 'delivered':
      // Due-windowed: lines DUE in the period that have a receipt — matches the
      // Delivered cost tile, keyed on Supplier Promise Date only.
      params['received'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'outstanding':
      // Trend "Outstanding" bar = LIVE outstanding value of lines DUE in the
      // period (open_value + arrears_value). Live = visibly open (On Hold /
      // Docked / WIP via open_view); status__in stays as the stale-outstanding
      // defense — Poeton closes lines short (status=received, stale
      // outstanding_qty), which would otherwise show as phantom outstanding.
      // Includes overdue (unlike 'open' which is the not-late snapshot).
      params['open_view'] = true;
      params['status__in'] = 'pending,partially_received';
      params['is_open'] = true;
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
    case 'otd':
      // OTD-mix donut slices — due-windowed on Supplier Promise Date only,
      // classified by the on_time_status extra filter.
      if (period?.start) params['due_from'] = period.start;
      if (period?.end) params['due_to'] = period.end;
      break;
  }
  return params;
}

/** Build a URL-safe query string for the "Open in PO Lines →" deep link. */
function buildPoLinesUrl(
  params: Record<string, string | number | boolean>,
): string {
  const search = new URLSearchParams();
  search.set('tab', 'po_lines');
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  return `/purchasing?${search.toString()}`;
}

/**
 * Derive the on-time classification for a line by comparing its actual
 * receipt date against the promised due date — the same rule the dashboard
 * OTD split uses. Returns the label + badge classes so the drill-down shows
 * *why* a line is Early/On time/Late (the comparison the user couldn't see).
 * ISO 'YYYY-MM-DD' dates compare correctly as strings.
 */
function deriveTiming(
  received?: string | null,
  due?: string | null,
): { label: string; cls: string } {
  // This portal ships goods OUT (Poeton is the supplier), so the labels are
  // always the outbound perspective: Delivered / Not delivered — never the
  // buyer-perspective Received / Not received. Label-only.
  if (!received) {
    return {
      label: 'Not delivered',
      cls: 'bg-slate-100 text-slate-500',
    };
  }
  if (!due) {
    return {
      label: 'Delivered',
      cls: 'bg-emerald-100 text-emerald-700',
    };
  }
  const r = received.slice(0, 10);
  const d = due.slice(0, 10);
  if (r < d) return { label: 'Early', cls: 'bg-blue-100 text-blue-700' };
  if (r > d) return { label: 'Late', cls: 'bg-red-100 text-red-700' };
  return { label: 'On time', cls: 'bg-emerald-100 text-emerald-700' };
}

/**
 * Right-hand drill-down side panel. Slides in from the right, fetches PO
 * lines matching the selected scope/period, and provides an "Open in PO
 * Lines" deep link. Renders gracefully on backend errors (e.g. before
 * Stream 1 ships).
 */
export function SalesOrdersDrillDown({
  isOpen,
  onClose,
  scope,
  period,
  extraFilters,
  title,
  subtitle,
  customerId,
  supplierId,
  partNumber,
  graceDays = 1,
}: SalesOrdersDrillDownProps) {
  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useFormatters();
  const { isSupplier } = usePortalMode();
  const { user } = useAuth();
  const counterpartyLabel = getCounterpartyLabel(isSupplier);
  // Supplier admins can set the per-line OTD override directly from the
  // drill-down. Customers never see it; the endpoint is supplier-only and the
  // panel self-gates on the platform 'allow line-level override' setting.
  const canOverrideOtd = isSupplier && !!user?.is_company_admin;

  const [overrideLine, setOverrideLine] = useState<PurchaseOrderLine | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // On-time sub-tab (All / On Time / Early / Late). Filters the fetched lines
  // client-side by receipt-vs-due timing.
  const [timingTab, setTimingTab] = useState<TimingTabKey>('all');
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = (key: SortKey) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // ---- Build the params we send to /purchasing/lines/ ----
  const queryParams = useMemo<Record<string, string | number | boolean>>(() => {
    if (!scope) return {};
    const params: Record<string, string | number | boolean> = {
      ...buildScopeParams(scope, period),
    };
    // Backend honours `customer` (->po__customer) and `company` (->po__supplier),
    // NOT customer_id/supplier_id — using the wrong keys silently dropped the
    // counterparty/site scoping.
    if (customerId) params['customer'] = String(customerId);
    if (supplierId !== undefined && supplierId !== '') params['company'] = Number(supplierId);
    if (partNumber) params['part_number'] = partNumber;
    if (extraFilters) {
      for (const [key, value] of Object.entries(extraFilters)) {
        if (value === undefined) continue;
        params[key] = value;
      }
    }
    // Load enough rows to actually investigate the slice in-modal (counts +
    // export reflect the real set, not a 50-line teaser). Covers every customer
    // scope fully; the very largest supplier scopes spill past this and are
    // served by "Open in PO Lines" (full paginated table) + the header total.
    params['page_size'] = 1000;
    params['page'] = 1;
    return params;
  }, [scope, period, customerId, supplierId, partNumber, extraFilters]);

  // ---- Fetch when opened ----
  useEffect(() => {
    if (!isOpen || !scope) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // If opened from an OTD slice / by-day bar, land on that on-time sub-tab so
    // the rows shown match the slice clicked (instead of resetting to All).
    const slice = extraFilters?.on_time_status;
    setTimingTab(slice === 'on_time' || slice === 'early' || slice === 'late' ? slice : 'all');
    setSort(null);

    purchasingAPI
      .getLines(queryParams)
      .then((resp) => {
        if (cancelled) return;
        setLines(resp.data.results || []);
        setCount(resp.data.count || 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load drill-down';
        setError(msg);
        setLines([]);
        setCount(0);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, scope, queryParams, refetchKey]);

  // Total line value for the header summary (received vs open vs total).
  const totalValue = useMemo(() => {
    if (!lines.length) return 0;
    return lines.reduce((acc, ln) => {
      const val = parseFloat(ln.total_price || '0');
      return acc + (Number.isFinite(val) ? val : 0);
    }, 0);
  }, [lines]);

  const totalOutstanding = useMemo(() => {
    if (!lines.length) return 0;
    return lines.reduce((acc, ln) => {
      const out = parseFloat(ln.outstanding_value || '0');
      return acc + (Number.isFinite(out) ? out : 0);
    }, 0);
  }, [lines]);

  // Per-bucket counts (of the fetched lines) for the sub-tab badges, and the
  // lines visible under the active sub-tab.
  const timingCounts = useMemo(() => {
    const c: Record<TimingTabKey, number> = { all: lines.length, on_time: 0, early: 0, late: 0 };
    for (const ln of lines) {
      const b = timingBucketOf(ln, graceDays);
      if (b !== 'other') c[b] += 1;
    }
    return c;
  }, [lines, graceDays]);

  const filteredLines = useMemo(() => {
    let base = timingTab === 'all'
      ? lines
      : lines.filter((ln) => timingBucketOf(ln, graceDays) === timingTab);
    // When opened from a deliveries-by-day bar, narrow to that weekday so the
    // drill-down matches the single bar clicked (the backend has no weekday
    // filter, so this is applied client-side over the fetched rows). Received
    // bars key on the receipt date; pending bars on the due date.
    const weekday = extraFilters?.weekday;
    if (typeof weekday === 'string' && weekday) {
      const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      base = base.filter((ln) => {
        const d = ln.dispatched_date || dueDateOf(ln);
        if (!d) return false;
        const dow = new Date(`${String(d).slice(0, 10)}T00:00:00Z`).getUTCDay();
        return DAYS[dow] === weekday;
      });
    }
    if (!sort) return base;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      const av = sortValueOf(a, sort.key);
      const bv = sortValueOf(b, sort.key);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }, [lines, timingTab, sort, extraFilters, graceDays]);

  // Excel export of the CURRENT tab (scope + active on-time bucket). Fetches
  // the full scope (not just the 50-row preview), applies the same on-time
  // filter as the visible tab, and writes an .xlsx mirroring the table columns.
  const handleExport = async () => {
    if (!scope) return;
    setExporting(true);
    try {
      const resp = await purchasingAPI.getLines({ ...queryParams, page_size: 2000, page: 1 });
      let rows = resp.data.results || [];
      if (timingTab !== 'all') {
        rows = rows.filter((ln) => timingBucketOf(ln, graceDays) === timingTab);
      }
      // Apply the same weekday narrowing as the on-screen table so the export
      // matches what the user sees (e.g. a single deliveries-by-day bar).
      const weekday = extraFilters?.weekday;
      if (typeof weekday === 'string' && weekday) {
        const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        rows = rows.filter((ln) => {
          const dt = ln.dispatched_date || dueDateOf(ln);
          if (!dt) return false;
          return DAYS[new Date(`${String(dt).slice(0, 10)}T00:00:00Z`).getUTCDay()] === weekday;
        });
      }
      const header = ['PO #', 'Line', 'Site', counterpartyLabel, 'Part', 'Supplier Promise Date', 'Delivered', 'On-time', 'Qty', 'Outstanding', 'Value', 'Status'];
      const aoa: (string | number)[][] = [
        header,
        ...rows.map((ln) => [
          ln.po_number || `PO ${ln.po}`,
          ln.line_number ?? '',
          ln.supplier_name || '',
          ln.customer_name || '',
          ln.part_number || '',
          (dueDateOf(ln)) ? formatDate(dueDateOf(ln) as string) : '',
          ln.dispatched_date ? formatDate(ln.dispatched_date) : '',
          deriveTiming(receiptDateOf(ln), dueDateOf(ln)).label,
          ln.quantity ?? '',
          ln.outstanding_qty ?? '',
          parseFloat(ln.total_price || '0'),
          stageLabelOf(ln).label,
        ]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lines');
      XLSX.writeFile(wb, `${scope}_${timingTab}_lines.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenInPoLines = () => {
    const url = buildPoLinesUrl(queryParams);
    navigate(url);
    onClose();
  };

  // Sortable column header cell.
  const sortTh = (k: SortKey, label: string, align?: 'right') => (
    <TableHead key={k} className={`h-9 text-xs whitespace-nowrap ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        <span>{label}</span>
        <span className="text-[9px] text-slate-400">{sort?.key === k ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </TableHead>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close drill-down"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 transition-opacity animate-in fade-in-0"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-[95vw] xl:max-w-[1500px] max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-200 overflow-hidden"
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900 truncate">{title}</h2>
              {subtitle && (
                <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>
              )}
              {period?.label && (
                <p className="text-[11px] text-slate-400 mt-1">Period: {period.label}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="gap-1.5"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInPoLines}
                className="gap-1.5"
              >
                Open in PO Lines
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label="Close"
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Summary line */}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-600">
            <div>
              <span className="font-medium tabular-nums text-slate-900">{count.toLocaleString()}</span>
              <span className="ml-1 text-slate-500">line{count === 1 ? '' : 's'}</span>
            </div>
            <div>
              <span className="text-slate-500">Total value: </span>
              <span className="font-medium tabular-nums text-slate-900">{formatCurrency(totalValue)}</span>
            </div>
            <div>
              <span className="text-slate-500">Outstanding: </span>
              <span className="font-medium tabular-nums text-slate-900">{formatCurrency(totalOutstanding)}</span>
            </div>
          </div>
        </div>

        {/* On-time sub-tabs */}
        <div className="flex-shrink-0 border-b border-slate-200 px-5">
          <div className="flex gap-1">
            {TIMING_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTimingTab(t.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  timingTab === t.key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
                <span className="ml-1 text-[10px] text-slate-400">{timingCounts[t.key]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Loading PO lines…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
              <p className="text-sm font-medium text-slate-700">Couldn't load this view</p>
              <p className="text-xs text-slate-500 mt-1 max-w-md">{error}</p>
              <p className="text-[11px] text-slate-400 mt-3">
                Some drill-down filters require backend support that may not be deployed yet.
              </p>
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <p className="text-sm font-medium text-slate-700">No matching PO lines</p>
              <p className="text-xs text-slate-500 mt-1">
                {timingTab === 'all' ? 'Try a different period or scope.' : 'No lines in this on-time bucket.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  {sortTh('po', 'PO #')}
                  {sortTh('line', 'Line')}
                  {sortTh('site', 'Site')}
                  {sortTh('counterparty', counterpartyLabel)}
                  {sortTh('part', 'Part')}
                  {sortTh('due', 'Supplier Promise Date')}
                  {sortTh('received', 'Delivered')}
                  {sortTh('ontime', 'On-time')}
                  {sortTh('qty', 'Qty', 'right')}
                  {sortTh('outstanding', 'Outstanding', 'right')}
                  {sortTh('value', 'Value', 'right')}
                  {sortTh('status', 'Status')}
                  {canOverrideOtd && (
                    <TableHead className="text-xs font-medium text-slate-600">OTD</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLines.map((ln) => {
                  const timing = deriveTiming(receiptDateOf(ln), dueDateOf(ln));
                  return (
                  <TableRow key={ln.id} className="even:bg-slate-50/60">
                    <TableCell className="py-2 text-xs font-medium whitespace-nowrap">
                      {ln.po_number ? (
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/purchasing?tab=po_lines&search=${encodeURIComponent(ln.po_number || '')}`);
                            onClose();
                          }}
                          className="text-blue-600 hover:underline"
                          title="Open this PO in PO Lines"
                        >
                          {ln.po_number}
                        </button>
                      ) : (
                        <span className="text-slate-800">PO {ln.po}</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700">
                      {ln.line_number}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700 max-w-[140px] truncate" title={ln.supplier_name || ''}>
                      {ln.supplier_name || '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700 max-w-[180px] truncate" title={ln.customer_name || ''}>
                      {ln.customer_name || '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700 max-w-[180px] truncate" title={ln.part_number}>
                      {ln.part_number}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700 whitespace-nowrap">
                      {(dueDateOf(ln)) ? formatDate(dueDateOf(ln) as string) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-slate-700 whitespace-nowrap">
                      {ln.dispatched_date ? formatDate(ln.dispatched_date) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${timing.cls}`}
                        title={receiptDateOf(ln) && dueDateOf(ln) ? `Delivered ${formatDate(receiptDateOf(ln) as string)} vs due ${formatDate(dueDateOf(ln) as string)}` : undefined}
                      >
                        {timing.label}
                      </span>
                    </TableCell>
                    <TableCell className="py-2 text-xs tabular-nums text-right text-slate-700">
                      {ln.quantity}
                    </TableCell>
                    <TableCell className="py-2 text-xs tabular-nums text-right text-slate-700">
                      {ln.outstanding_qty}
                    </TableCell>
                    <TableCell className="py-2 text-xs tabular-nums text-right text-slate-700">
                      {formatCurrency(parseFloat(ln.total_price || '0'))}
                    </TableCell>
                    <TableCell className="py-2 text-xs">
                      {(() => {
                        // Mirror the SO Lines grid Status column: DNA processing
                        // stage with On Hold overlaid from the flag.
                        const stage = ln.on_hold ? 'on_hold' : (ln.processing_stage || '');
                        const cfg = PROCESSING_STAGE_CONFIG[stage];
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${cfg?.className || 'bg-slate-100 text-slate-700'}`}>
                            {cfg?.label || ln.processing_stage || '—'}
                          </span>
                        );
                      })()}
                    </TableCell>
                    {canOverrideOtd && (
                      <TableCell className="py-2 text-xs whitespace-nowrap">
                        <Button
                          size="sm"
                          variant={ln.otd_overridden ? 'default' : 'outline'}
                          className="h-6 px-2 text-[10px] gap-1"
                          onClick={() => setOverrideLine(ln)}
                          title={ln.otd_overridden
                            ? `OTD overridden to ${ln.otd_override_value ? 'On time' : 'Late'}`
                            : 'Set OTD override for this line'}
                        >
                          <ShieldAlert className="h-3 w-3" />
                          {ln.otd_overridden ? 'Overridden' : 'Override'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-200 px-5 py-2.5">
          <p className="text-[11px] text-slate-400">
            {count > lines.length
              ? `Showing ${lines.length} of ${count.toLocaleString()} lines — Export (top) or open in PO Lines for the full set.`
              : `Showing all ${count.toLocaleString()} line${count === 1 ? '' : 's'}.`}
          </p>
        </div>
      </div>

      {/* OTD override dialog (supplier admins only). Saving refetches the
          drill-down so the line re-classifies into its new slice. */}
      <Dialog open={!!overrideLine} onOpenChange={(o) => { if (!o) setOverrideLine(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              OTD override — {overrideLine?.po_number ? `PO ${overrideLine.po_number}` : `PO ${overrideLine?.po}`}
              {overrideLine ? ` · line ${overrideLine.line_number}` : ''}
            </DialogTitle>
          </DialogHeader>
          {overrideLine && (
            <OTDOverridePanel
              line={overrideLine}
              onUpdate={() => { setOverrideLine(null); setRefetchKey((k) => k + 1); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
